import { Box } from '@mui/material';
import { useRef, useEffect, ChangeEvent } from 'react';
import BaseTextField from './BaseTextField';
import { useTranslation } from 'react-i18next';

interface CaptchaCanvasProps {
    captchaKey: string;
    isCaptchaError: boolean;
    errorMessage?: string;
    onRefreshCaptcha?: () => void;
    setCaptcha: (captcha: string) => void;
}

export default function CaptchaCanvas({
    captchaKey,
    isCaptchaError,
    errorMessage,
    onRefreshCaptcha,
    setCaptcha,
}: CaptchaCanvasProps) {
    const { t } = useTranslation('common');
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    function addBackgroundNoise(ctx: CanvasRenderingContext2D, width: number, height: number) {
        const numberOfDots = 100;
        for (let i = 0; i < numberOfDots; i++) {
            ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
            ctx.beginPath();
            ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 3, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    function drawDistortedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number) {
        let cumulativeWidth = 0; // Initialize cumulative width

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            ctx.save();

            // Calculate width of all characters up to current one
            if (i > 0) {
                const prevChar = text[i - 1];
                cumulativeWidth += ctx.measureText(prevChar).width + 0.5;
            }

            // Apply slight rotation for each character
            const rotation = (Math.random() - 0.5) * 0.3; // Adjust rotation degree here
            ctx.translate(x + cumulativeWidth, y); // Move to the position where the character will be drawn
            ctx.rotate(rotation);

            // Draw character at the new origin (0,0) after translation
            ctx.fillText(char, 0, 0);

            ctx.restore();
        }
    }
    function addOverlayLines(ctx: CanvasRenderingContext2D, width: number, height: number) {
        const lines = 5;
        ctx.beginPath();
        for (let i = 0; i < lines; i++) {
            ctx.moveTo(Math.random() * width, Math.random() * height);
            ctx.lineTo(Math.random() * width, Math.random() * height);
            ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.8)`;
            ctx.stroke();
        }
    }

    const inputCaptchaHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCaptcha(e.target.value);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.width = 200;
        canvas.height = 50;

        // Fill background
        ctx.fillStyle = '#EEE';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add noise to the background
        addBackgroundNoise(ctx, canvas.width, canvas.height);

        // Draw text
        ctx.font = '30px monospace';
        ctx.fillStyle = 'black';

        // Calculate text width
        const textWidth = ctx.measureText(captchaKey).width;

        // Calculate positions to center the text
        const x = (canvas.width - textWidth) / 2;
        const y = canvas.height / 2 + ctx.measureText('M').width / 2 - 2; // Roughly center vertically
        drawDistortedText(ctx, captchaKey, x, y);
        // Draw centered text
        // ctx.fillText(captchaKey, x, y);
        addOverlayLines(ctx, canvas.width, canvas.height);

        // Note: For real captcha, consider adding more visual complexity
    }, [captchaKey]);

    return (
        <>
            <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                <p>{t('Please input the captcha in 5 minutes to confirm the deletion:')}</p>
                <Box sx={{ mb: 2 }}>
                    <canvas
                        ref={canvasRef}
                        onClick={onRefreshCaptcha}
                    />
                </Box>
                <BaseTextField
                    autoFocus
                    variant="outlined"
                    placeholder={t('Enter Captcha')}
                    fullWidth
                    error={isCaptchaError || Boolean(errorMessage)}
                    helperText={errorMessage || (isCaptchaError ? t('Captcha Not Matched!') : '')}
                    onChange={inputCaptchaHandler}
                />
            </Box>
        </>
    );
}
